/**
 * Non-drag reordering: move an item one step up/down in a list. Returns the new
 * order and the anchor to send to the backend (it computes the position key).
 */
export type StepAnchor<Prefix extends string> =
  Record<`after_${Prefix}_id`, string> | Record<`before_${Prefix}_id`, string>;

export function stepMove<Prefix extends string>(
  ids: readonly string[],
  id: string,
  direction: 'up' | 'down',
  prefix: Prefix,
): { order: string[]; anchor: StepAnchor<Prefix> } | null {
  const index = ids.indexOf(id);
  const neighborIndex = direction === 'up' ? index - 1 : index + 1;
  const neighbor = ids[neighborIndex];
  if (index === -1 || neighbor === undefined) return null;
  const order = [...ids];
  order[index] = neighbor;
  order[neighborIndex] = id;
  const anchor = (
    direction === 'up'
      ? { [`before_${prefix}_id`]: neighbor }
      : { [`after_${prefix}_id`]: neighbor }
  ) as StepAnchor<Prefix>;
  return { order, anchor };
}

/**
 * Drag-and-drop reorder: put `id` before/after `targetId`. Returns the new
 * order and the backend anchor, or `null` when nothing would change.
 */
export function dropMove<Prefix extends string>(
  ids: readonly string[],
  id: string,
  targetId: string,
  position: 'before' | 'after',
  prefix: Prefix,
): { order: string[]; anchor: StepAnchor<Prefix> } | null {
  if (id === targetId || !ids.includes(id) || !ids.includes(targetId)) return null;
  const rest = ids.filter((x) => x !== id);
  const index = rest.indexOf(targetId) + (position === 'after' ? 1 : 0);
  const order = [...rest.slice(0, index), id, ...rest.slice(index)];
  if (order.every((x, i) => x === ids[i])) return null;
  const anchor = (
    position === 'before'
      ? { [`before_${prefix}_id`]: targetId }
      : { [`after_${prefix}_id`]: targetId }
  ) as StepAnchor<Prefix>;
  return { order, anchor };
}
